import React, { useEffect, useState } from 'react'
import { Card, CardBody } from 'react-bootstrap'
import { useAuth } from '../../../../contexts/AuthContext';
import { IATSItem } from '../../../../component/solution/Interfaces';
import { Download } from 'lucide-react';
import ShowErrorAlert from '../../../../component/ShowErrorAlert';
import { formatDateFull } from '../../../../component/solution/utils/AppUtil';

export const ATSSummary:React.FC = () => {

  const { fetchData, showErrorAlert, responseErrorMessage, responseStatus } = useAuth();

  const planningATSQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/PLANNING_TRIGGER/run`;
  const summaryQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/content/list?path=content/ats-summary`;

  const [data, setData] = useState<IATSItem[] | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [downloadingFileName, setDownloadingFileName] = useState<string | null>(null);
  const [shouldFadeOut, setShouldFadeOut] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  
  //every 60 seconds 
  useEffect(() => {
    fetchSummary(); // initial fetch

    const interval = setInterval(() => {
        fetchSummary(); // repeat every 60s
    }, 60000); // 60000 ms = 60 seconds

    return () => clearInterval(interval); // cleanup on unmount
  }, []);

  // preventing the download spinner from spinning
  useEffect(() => {
      if(responseStatus === "400" || responseStatus === "401"){
          setDownloading(false);
      }
      if(showErrorAlert){
          setDownloading(false);
      }        
  }, [responseStatus, showErrorAlert])

    useEffect(() => {
        if (downloadComplete && !downloading) {
            // Start fading after 4.5s
            const fadeTimer = setTimeout(() => {
                setShouldFadeOut(true);
            }, 4500);

            // Hide alert completely after 5s
            const hideTimer = setTimeout(() => {
                setDownloadComplete(false);
                setShouldFadeOut(false); // reset for future
            }, 5000);

            return () => {
                clearTimeout(fadeTimer);
                clearTimeout(hideTimer);
            };
        }
  }, [downloadComplete, downloading]);


  const fetchSummary = () => {
    fetchData(summaryQueryUrl, { method: "GET" }, processSummaryData);
  };

  const processSummaryData = (result: any) => {
      if (!Array.isArray(result)) {
        console.error("Expected array but got:", result);
        return;
      }

      const items: IATSItem[] = result.map((entry: any) => ({...entry}));

      setData(items);
  };


  const handleExport = (item: IATSItem) => {
      if (!item) return;

      setDownloading(true);
      setDownloadingFileName(item.name);

      const fileUrl = `${process.env.REACT_APP_COMPANY_URL}${item.path}`;
      const fileName = item.name;

      fetchData(fileUrl, { method: "GET" }, (result: any, status: string) => {
          if (status === "200" || status === "201") {
              handleExcelExport(result, fileName);
              setDownloadComplete(true);  
          } else {
              setDownloading(false);      
          }

          setDownloading(false);
          setDownloadingFileName(null);
      });
  };


  const handleExcelExport = (result: Blob, fileName?: string) => {
    
    if (!fileName) {
      if (result.type === "text/csv") {
        fileName = "download.csv"; 
      } else if (result.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        fileName = "download.xlsx";
      } else {
        fileName = "download"; // fallback with no extension
      }
    }

    const url = URL.createObjectURL(result);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    URL.revokeObjectURL(url);
    a.remove();
  };


const handleRefresh = () => {
    setRefreshing(true);

    const MIN_DURATION = 2 * 60 * 1000; // 2 minutes
    const startTime = Date.now();

    fetchData(planningATSQueryUrl, { method: "POST" }, (result: any, status: string) => {
    const elapsed = Date.now() - startTime;
    const delay = Math.max(0, MIN_DURATION - elapsed);

    setTimeout(() => {
        console.log(result, status);
        setRefreshing(false);
    }, delay);
    });
};


const thStyle = {
    border: '1px solid #deebed',
    color: "#6c757d",
    padding: "8px",
    whiteSpace: "nowrap" as const,
    backgroundColor: "#f8f9fa",
};

const tdStyle = {
    borderBottom: "1px solid #dee6ed",
    borderRight: "1px solid #dee6ed",
    padding: "8px",
    whiteSpace: "nowrap" as const,
};
  

  return (
    <>
        { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 

        { downloadComplete && !downloading && (
          <div className={`alert d-flex align-items-center py-2 px-3 mb-2 ${shouldFadeOut ? 'fading-out' : ''}`} role="alert" 
                style={{ backgroundColor: "#c7f5bf", border: "1px solid rgb(222, 235, 237)", color: "#037012", transition: "opacity 0.5s ease-in-out", opacity: shouldFadeOut ? 0 : 1 }}>
                <span className="flex-grow-1 text-center">
                  Your report has been successfully downloaded.
                </span>
                <button type="button" 
                  style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', backgroundColor: '#c7f5bf', color: '#e53e3e', borderRadius: '8px', padding: '6px', height: '32px', width: '32px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                  aria-label="Close"
                  onClick={() => setDownloadComplete(false)} 
                >
                  <svg style={{ width: '12px', height: '12px' }} aria-hidden="true" xmlns="http://www.w3.org/2000/svg"  fill="none" viewBox="0 0 14 14">
                    <path stroke="#037012" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                  </svg>
              </button>
          </div>
      )}

      {downloadComplete && downloading && (
          <div className="alert d-flex align-items-center py-2 px-3 mb-2" role="alert"  style={{backgroundColor: "#e6eefa", border: '1px solid rgb(222, 235, 237)', color: "#035a70"}}>
              <span className="flex-grow-1 text-center">
              Your report is currently being processed. Please wait...
              </span>
          </div>
      )}

    <Card>
           {/* <div className="d-flex flex-column align-items-center justify-content-center mb-1 p-2">
          <button
            className="btn btn-light w-100 d-flex align-items-center justify-content-center"
            style={{
              fontWeight: 500,
              fontSize: "13px",
              padding: "0.4rem 0.4rem",
              maxWidth: "100%",
            }}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Preparing your reports...
              </>
            ) : (
              "Refresh ATS Reports"
            )}
          </button>

          {refreshing && (
            <small className="text-muted mt-2">
              This might take a few minutes. Please wait...
            </small>
          )}
        </div> */}
        <Card.Header className="pb-0"> 
          <Card.Title >{`ATS Summary`}</Card.Title> 
        </Card.Header>
        <CardBody className="pt-3">
        <div style={{ overflowY: "auto" , marginBottom: "4px", position: "relative", backgroundColor: "#fff", borderBottom: "1px solid #dee6ed" }}>
         <table className="table table-bordered table-md">
          <thead>
            <tr>
              <th style={thStyle}>File Name</th>
              <th style={thStyle}>Last Modified</th>
              <th style={{...thStyle, width: "100px"}}>Export File</th>
            </tr>
          </thead>
            <tbody>
            {data?.length
              ? data.map((item, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{item.name}</td>
                    <td style={tdStyle}>{formatDateFull(item.lastModified)}</td>
                    <td style={tdStyle}>
                      <div title={downloading ? "Preparing report..." : "Export"}>
                        <button
                          className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                          onClick={() => handleExport(item)}
                          disabled={downloading}
                          style={{
                            position: "relative",
                            padding:
                              downloading && downloadingFileName === item.name
                                ? "0.39rem 0.65rem"
                                : "0.4rem 0.65rem",
                          }}
                        >
                          {downloading && downloadingFileName === item.name ? (
                            <span
                              className="spinner-border spinner-border-sm custom-spinner"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            <Download size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              : null}
            </tbody>
          </table>
        </div>
       </CardBody>
     </Card>
    </>
  );
}
